import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SocialSignIn } from "@/components/SocialSignIn";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)] px-4">
      <div className="w-full max-w-sm">
        <h1 className="font-sacred text-3xl text-center text-[var(--brand-navy)] dark:text-amber-100 tracking-wide mb-8">Kindler</h1>
        <div className="bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-8 space-y-5 shadow-sm">{children}</div>
      </div>
    </div>
  );
}

export default function JoinMinistry() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [joining, setJoining] = useState(false);
  const next = `/join/${token}`;

  useEffect(() => {
    api.get(`/orgs/join/${token}`).then(({ data }) => setInfo(data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token]);

  const join = async () => {
    setJoining(true);
    try {
      const { data } = await api.post(`/orgs/join/${token}`);
      toast.success(data.already_member ? `You're already in ${data.ministry_name}` : `Welcome to ${data.ministry_name}`);
      navigate("/ministry");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setJoining(false);
    }
  };

  if (error) return <Shell><p className="text-sm text-center text-[var(--brand-crimson)]" data-testid="join-error">{error}</p></Shell>;
  if (!info || user === null) return <Shell><p className="text-center font-display italic text-muted-foreground" data-testid="join-loading">Loading...</p></Shell>;

  return (
    <Shell>
      <div className="text-center space-y-1" data-testid="join-ministry-info">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">You're invited to join</p>
        <p className="font-display text-2xl text-[var(--brand-navy)] dark:text-amber-100" data-testid="join-ministry-name">{info.ministry_name}</p>
        {info.parish_name && <p className="text-sm text-muted-foreground" data-testid="join-parish-name">{info.parish_name}</p>}
      </div>
      {user ? (
        <Button onClick={join} disabled={joining} className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" data-testid="join-ministry-button">
          {joining ? "Joining..." : `Join as ${user.name}`}
        </Button>
      ) : (
        <>
          <SocialSignIn mode="up" next={next} />
          <div className="flex gap-2">
            <Button asChild variant="outline" className="flex-1"><Link to={`/login?next=${encodeURIComponent(next)}`} data-testid="join-email-login-link">Sign in</Link></Button>
            <Button asChild variant="outline" className="flex-1"><Link to={`/register?next=${encodeURIComponent(next)}`} data-testid="join-email-register-link">Register</Link></Button>
          </div>
        </>
      )}
    </Shell>
  );
}
